import { useRef, useState, type FormEvent } from 'react';
import { Link, useNavigate, useOutletContext } from 'react-router';
import { useAgents, useCatalog, useCreateDecree, useDecrees, useSignDecree } from '../api/hooks';
import { openAuthenticatedFile } from '../api/client';
import { ErrorNote, Loading, PageHead, StatusBadge, formatDate } from '../components/ui';

interface MeContext {
  roles: Array<{ role: string }>;
}

export function DecreesPage() {
  const me = useOutletContext<MeContext>();
  const isDrh = me.roles.some((r) => r.role === 'drh');
  const decrees = useDecrees();
  const agents = useAgents();
  const types = useCatalog('decree-types');
  const sign = useSignDecree();
  const dialog = useRef<HTMLDialogElement>(null);
  const [signing, setSigning] = useState<{ id: string; subject: string } | null>(null);
  const [signedOn, setSignedOn] = useState(new Date().toISOString().slice(0, 10));
  const [signedBy, setSignedBy] = useState('Le Maire');

  const agentName = (id: string) => {
    const a = agents.data?.find((x) => x.id === id);
    return a ? `${a.lastName.toUpperCase()} ${a.firstName}` : '…';
  };
  const typeLabel = (id: string) => types.data?.find((t) => t.id === id)?.label ?? '…';

  const openSign = (id: string, subject: string) => {
    setSigning({ id, subject });
    sign.reset();
    dialog.current?.showModal();
  };
  const submitSign = (e: FormEvent) => {
    e.preventDefault();
    if (!signing) return;
    sign.mutate({ id: signing.id, signedOn, signedBy }, { onSuccess: () => dialog.current?.close() });
  };

  return (
    <>
      <PageHead
        eyebrow="Actes"
        title="Arrêtés"
        actions={
          isDrh && (
            <Link className="btn btn-primary" to="/decrees/new">
              Nouvel arrêté
            </Link>
          )
        }
      >
        Un brouillon reste modifiable. La signature attribue le numéro, produit le PDF et fige l'acte.
      </PageHead>
      <ErrorNote error={decrees.error} />
      {decrees.isPending ? (
        <Loading />
      ) : (
        <div className="tw">
          <table>
            <thead>
              <tr>
                <th>Numéro</th>
                <th>Type</th>
                <th>Agent</th>
                <th>Objet</th>
                <th>Effet</th>
                <th>État</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {(decrees.data ?? []).length === 0 && (
                <tr>
                  <td colSpan={7} className="empty">
                    Aucun arrêté dans votre périmètre.
                  </td>
                </tr>
              )}
              {(decrees.data ?? []).map((d) => (
                <tr key={d.id}>
                  <td className="mono">{d.number ?? '—'}</td>
                  <td>{typeLabel(d.typeId)}</td>
                  <td>{agentName(d.agentId)}</td>
                  <td>{d.subject}</td>
                  <td>{formatDate(d.effectiveOn)}</td>
                  <td>
                    <StatusBadge status={d.status} />
                  </td>
                  <td className="actions">
                    {d.status === 'draft' && isDrh && (
                      <button className="btn btn-sm btn-primary" onClick={() => openSign(d.id, d.subject)}>
                        Signer
                      </button>
                    )}
                    {d.status === 'signed' && (
                      <button className="btn btn-sm" onClick={() => void openAuthenticatedFile(`/api/v1/decrees/${d.id}/pdf`)}>
                        PDF
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <dialog ref={dialog} aria-labelledby="sign-title">
        <form onSubmit={submitSign}>
          <h2 id="sign-title">Signer l'arrêté</h2>
          <p className="muted">{signing?.subject}</p>
          <div className="note warn small">Après signature, l'arrêté ne pourra plus être modifié ni supprimé. Toute correction passera par un nouvel arrêté.</div>
          <div className="field">
            <label htmlFor="signed-on">Date de signature</label>
            <input id="signed-on" type="date" required value={signedOn} onChange={(e) => setSignedOn(e.target.value)} />
          </div>
          <div className="field">
            <label htmlFor="signed-by">Signataire</label>
            <input id="signed-by" required value={signedBy} onChange={(e) => setSignedBy(e.target.value)} placeholder="Le Maire, ou par délégation…" />
          </div>
          <ErrorNote error={sign.error} />
          <div className="actions">
            <button type="submit" className="btn btn-primary" disabled={sign.isPending}>
              {sign.isPending ? 'Signature et PDF…' : 'Signer et produire le PDF'}
            </button>
            <button type="button" className="btn" onClick={() => dialog.current?.close()}>
              Annuler
            </button>
          </div>
        </form>
      </dialog>
    </>
  );
}

export function NewDecreePage() {
  const navigate = useNavigate();
  const agents = useAgents();
  const types = useCatalog('decree-types');
  const create = useCreateDecree();
  const [form, setForm] = useState({ agentId: '', typeId: '', subject: '', visas: '', articles: '', effectiveOn: '' });
  const set = (k: keyof typeof form) => (e: { target: { value: string } }) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const lines = (s: string) => s.split('\n').map((l) => l.trim()).filter(Boolean);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    create.mutate(
      {
        agentId: form.agentId,
        typeId: form.typeId,
        subject: form.subject,
        visas: lines(form.visas),
        articles: lines(form.articles),
        effectiveOn: form.effectiveOn || null,
      },
      { onSuccess: () => void navigate('/decrees') },
    );
  };

  return (
    <>
      <PageHead eyebrow="Actes" title="Nouvel arrêté" actions={<Link to="/decrees">← Tous les arrêtés</Link>}>
        Le brouillon est libre. Une ligne par visa et par article.
      </PageHead>
      <form className="card" onSubmit={submit} style={{ maxWidth: 820 }}>
        <div className="form-grid">
          <div className="field">
            <label htmlFor="d-agent">Agent</label>
            <select id="d-agent" required value={form.agentId} onChange={set('agentId')}>
              <option value="">— Choisir —</option>
              {(agents.data ?? []).map((a) => (
                <option key={a.id} value={a.id}>
                  {a.lastName.toUpperCase()} {a.firstName} · {a.matricule}
                </option>
              ))}
            </select>
          </div>
          <div className="field">
            <label htmlFor="d-type">Type d'arrêté</label>
            <select id="d-type" required value={form.typeId} onChange={set('typeId')}>
              <option value="">— Choisir —</option>
              {(types.data ?? []).map((t) => (
                <option key={t.id} value={t.id}>
                  {t.label}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="field">
          <label htmlFor="d-subject">Objet</label>
          <input id="d-subject" required maxLength={300} value={form.subject} onChange={set('subject')} />
        </div>
        <div className="field">
          <label htmlFor="d-visas">Visas</label>
          <textarea id="d-visas" value={form.visas} onChange={set('visas')} placeholder={"le statut général des fonctionnaires des communes de la Polynésie française\nla délibération n° … du …"} />
          <span className="hint">Chaque ligne sera précédée de « Vu ».</span>
        </div>
        <div className="field">
          <label htmlFor="d-articles">Articles</label>
          <textarea id="d-articles" required value={form.articles} onChange={set('articles')} placeholder={"M. … est recruté en qualité de …\nLe directeur général des services est chargé de l'exécution du présent arrêté."} />
          <span className="hint">Chaque ligne devient un article numéroté.</span>
        </div>
        <div className="field" style={{ maxWidth: 240 }}>
          <label htmlFor="d-effect">Prise d'effet</label>
          <input id="d-effect" type="date" value={form.effectiveOn} onChange={set('effectiveOn')} />
        </div>
        <ErrorNote error={create.error} />
        <div className="actions">
          <button type="submit" className="btn btn-primary" disabled={create.isPending}>
            Enregistrer le brouillon
          </button>
        </div>
      </form>
    </>
  );
}
